import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sendNotifications } from '../../services/emailService';

type TabType = 'perfil' | 'disponibilidad' | 'procesos' | 'recursos' | 'editar';

export default function PerfilPage() {
  const { user, isAuthenticated, authLoading, updateUserProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('perfil');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isRequestingAccess, setIsRequestingAccess] = useState(false);

  const [formData, setFormData] = useState({
    nombre: user?.nombre || '',
    sector: user?.sector || '',
    experiencia: user?.experiencia || '',
    pais: user?.pais || '',
    presentacion: '',
    disponibilidad: user?.disponibilidad || 'disponible',
    disponibilidadGeografica: user?.disponibilidadGeografica || [],
  });

  useEffect(() => {
    // Esperar a que termine de verificar la sesión
    if (authLoading) return;

    if (!isAuthenticated || user?.userType !== 'candidato') {
      navigate('/');
      return;
    }

    const tab = searchParams.get('tab') as TabType;
    if (tab && ['perfil', 'disponibilidad', 'procesos', 'recursos', 'editar'].includes(tab)) {
      setActiveTab(tab);
      if (tab === 'editar') {
        setIsEditing(true);
      }
    }
  }, [isAuthenticated, user, navigate, searchParams, authLoading]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGeografiaToggle = (pais: string) => {
    setFormData(prev => ({
      ...prev,
      disponibilidadGeografica: prev.disponibilidadGeografica.includes(pais)
        ? prev.disponibilidadGeografica.filter(p => p !== pais)
        : [...prev.disponibilidadGeografica, pais]
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateUserProfile(formData);
      setShowSuccessMessage(true);
      setIsEditing(false);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error al guardar:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const paisesDisponibles = [
    { code: 'DE', name: 'Alemania', flag: '🇩🇪' },
    { code: 'CH', name: 'Suiza', flag: '🇨🇭' },
    { code: 'NL', name: 'Países Bajos', flag: '🇳🇱' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹' },
    { code: 'BE', name: 'Bélgica', flag: '🇧🇪' },
  ];

  const disponibilidadOptions = [
    { value: 'disponible', label: 'Disponible para procesos', color: 'green', icon: 'ri-checkbox-circle-line' },
    { value: 'en-proceso', label: 'En proceso activo', color: 'blue', icon: 'ri-time-line' },
    { value: 'no-disponible', label: 'No disponible temporalmente', color: 'gray', icon: 'ri-close-circle-line' },
  ];

  const recursosEstrategicos = [
    {
      id: 1,
      titulo: 'Adaptar el CV al mercado europeo',
      descripcion: 'Estructura tu currículum según los estándares europeos y destaca en procesos de selección internacional.',
      icon: 'ri-file-text-line',
      ruta: '/guia-cv-europeo',
    },
    {
      id: 2,
      titulo: 'Preparar entrevistas internacionales',
      descripcion: 'Estrategias y técnicas para afrontar entrevistas con empresas europeas con confianza y criterio.',
      icon: 'ri-user-voice-line',
      ruta: '/guia-entrevistas-internacionales',
    },
    {
      id: 3,
      titulo: 'Claves culturales en Alemania',
      descripcion: 'Puntualidad, jerarquía, comunicación directa y expectativas profesionales en el entorno laboral alemán.',
      icon: 'ri-flag-line',
      ruta: '/guia-claves-alemania',
      flag: '🇩🇪',
    },
    {
      id: 4,
      titulo: 'Claves culturales en Suiza',
      descripcion: 'Precisión, neutralidad, multilingüismo y protocolos de trabajo en un entorno corporativo internacional.',
      icon: 'ri-flag-line',
      ruta: '/guia-claves-suiza',
      flag: '🇨🇭',
    },
    {
      id: 5,
      titulo: 'Claves culturales en Países Bajos',
      descripcion: 'Horizontalidad, consenso, equilibrio vida-trabajo y comunicación abierta en el modelo holandés.',
      icon: 'ri-flag-line',
      ruta: '/guia-claves-paises-bajos',
      flag: '🇳🇱',
    },
    {
      id: 6,
      titulo: 'Claves culturales en Austria',
      descripcion: 'Formalidad, calidad, tradición y excelencia profesional en el mercado laboral austriaco.',
      icon: 'ri-flag-line',
      ruta: '/guia-claves-austria',
      flag: '🇦🇹',
    },
    {
      id: 7,
      titulo: 'Claves culturales en Bélgica',
      descripcion: 'Bilingüismo, diversidad regional, instituciones europeas y networking en el entorno multicultural belga.',
      icon: 'ri-flag-line',
      ruta: '/guia-claves-belgica',
      flag: '🇧🇪',
    },
  ];

  const handleRequestLibraryAccess = async () => {
    if (!user) return;

    setIsRequestingAccess(true);
    try {
      await sendNotifications(
        'Solicitud de Acceso Biblioteca',
        {
          nombre: user.nombre || 'No especificado',
          email: user.email,
          sector: user.sector || 'No especificado',
          experiencia: user.experiencia || 'No especificado',
          pais: user.pais || 'No especificado',
          fecha: new Date().toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }),
        },
        user.email,
        user.nombre || 'Candidato'
      );

      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 4000);
    } catch (error) {
      console.error('Error al solicitar acceso:', error);
    } finally {
      setIsRequestingAccess(false);
    }
  };

  // Mostrar spinner mientras verifica la sesión
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#1A2B4A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="fixed top-24 right-6 z-50 bg-green-50 border border-green-200 rounded-lg px-6 py-4 shadow-lg animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <i className="ri-check-line text-green-600 text-xl"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-green-900">Solicitud enviada</p>
              <p className="text-xs text-green-700">Te contactaremos pronto para activar tu acceso</p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Confidentiality Banner */}
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-6 mb-8 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-white text-2xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Confidencialidad garantizada</h3>
              <p className="text-sm text-white/90 leading-relaxed">
                Tu perfil profesional se gestiona con total confidencialidad. La información solo se comparte con empresas tras una validación interna rigurosa y únicamente cuando exista una vacante alineada con tu perfil y expectativas.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6 overflow-hidden">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => {
                setActiveTab('perfil');
                setIsEditing(false);
              }}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'perfil'
                  ? 'bg-[#1A2B4A] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-user-line mr-2"></i>
              Mi perfil
            </button>
            <button
              onClick={() => {
                setActiveTab('disponibilidad');
                setIsEditing(false);
              }}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'disponibilidad'
                  ? 'bg-[#1A2B4A] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-checkbox-circle-line mr-2"></i>
              Estado de disponibilidad
            </button>
            <button
              onClick={() => {
                setActiveTab('procesos');
                setIsEditing(false);
              }}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'procesos'
                  ? 'bg-[#1A2B4A] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-briefcase-line mr-2"></i>
              Mis procesos
            </button>
            <button
              onClick={() => {
                setActiveTab('recursos');
                setIsEditing(false);
              }}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'recursos'
                  ? 'bg-[#1A2B4A] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-book-open-line mr-2"></i>
              Recursos estratégicos
            </button>
            <button
              onClick={() => {
                setActiveTab('editar');
                setIsEditing(true);
              }}
              className={`flex-1 px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'editar'
                  ? 'bg-[#1A2B4A] text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-edit-line mr-2"></i>
              Editar información
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          {/* Perfil Tab */}
          {activeTab === 'perfil' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-6">Información profesional</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Nombre completo</p>
                    <p className="text-base font-semibold text-gray-900">{user.nombre || 'No especificado'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Correo electrónico</p>
                    <p className="text-base font-semibold text-gray-900">{user.email}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Sector profesional</p>
                    <p className="text-base font-semibold text-gray-900">{user.sector || 'No especificado'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Años de experiencia</p>
                    <p className="text-base font-semibold text-gray-900">{user.experiencia || 'No especificado'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">País de residencia</p>
                    <p className="text-base font-semibold text-gray-900">{user.pais || 'No especificado'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                    <p className="text-xs font-medium text-gray-500 mb-1">Estado actual</p>
                    <div className="flex items-center gap-2 mt-1">
                      {user.disponibilidad === 'disponible' && (
                        <>
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          <span className="text-base font-semibold text-green-700">Disponible para procesos</span>
                        </>
                      )}
                      {user.disponibilidad === 'en-proceso' && (
                        <>
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                          <span className="text-base font-semibold text-blue-700">En proceso activo</span>
                        </>
                      )}
                      {user.disponibilidad === 'no-disponible' && (
                        <>
                          <span className="w-2 h-2 bg-gray-500 rounded-full"></span>
                          <span className="text-base font-semibold text-gray-700">No disponible temporalmente</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Disponibilidad geográfica</h3>
                {user.disponibilidadGeografica && user.disponibilidadGeografica.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {user.disponibilidadGeografica.map((pais) => {
                      const paisInfo = paisesDisponibles.find(p => p.code === pais);
                      return (
                        <div key={pais} className="bg-[#1A2B4A] text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2">
                          <span className="text-lg">{paisInfo?.flag}</span>
                          {paisInfo?.name}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 border border-gray-200">
                    No has especificado disponibilidad geográfica
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Disponibilidad Tab */}
          {activeTab === 'disponibilidad' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Estado de disponibilidad</h2>
                <p className="text-sm text-gray-600 mb-6">
                  Indica tu situación actual para que podamos contactarte en el momento adecuado
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {disponibilidadOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setFormData(prev => ({ ...prev, disponibilidad: option.value as any }));
                        updateUserProfile({ disponibilidad: option.value as any });
                        setShowSuccessMessage(true);
                        setTimeout(() => setShowSuccessMessage(false), 3000);
                      }}
                      className={`p-6 rounded-xl border-2 transition-all cursor-pointer ${
                        formData.disponibilidad === option.value
                          ? `border-${option.color}-500 bg-${option.color}-50`
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                        formData.disponibilidad === option.value
                          ? `bg-${option.color}-100`
                          : 'bg-gray-100'
                      }`}>
                        <i className={`${option.icon} text-2xl ${
                          formData.disponibilidad === option.value
                            ? `text-${option.color}-600`
                            : 'text-gray-600'
                        }`}></i>
                      </div>
                      <p className={`text-sm font-semibold ${
                        formData.disponibilidad === option.value
                          ? `text-${option.color}-900`
                          : 'text-gray-900'
                      }`}>
                        {option.label}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Disponibilidad geográfica</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Selecciona los países donde estarías dispuesto a trabajar
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {paisesDisponibles.map((pais) => (
                    <button
                      key={pais.code}
                      onClick={() => {
                        handleGeografiaToggle(pais.code);
                        const newGeo = formData.disponibilidadGeografica.includes(pais.code)
                          ? formData.disponibilidadGeografica.filter(p => p !== pais.code)
                          : [...formData.disponibilidadGeografica, pais.code];
                        updateUserProfile({ disponibilidadGeografica: newGeo });
                        setShowSuccessMessage(true);
                        setTimeout(() => setShowSuccessMessage(false), 3000);
                      }}
                      className={`p-5 rounded-xl border-2 transition-all cursor-pointer flex items-center gap-4 ${
                        formData.disponibilidadGeografica.includes(pais.code)
                          ? 'border-[#1A2B4A] bg-[#1A2B4A]/5'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-2xl ${
                        formData.disponibilidadGeografica.includes(pais.code)
                          ? 'bg-[#1A2B4A]/10'
                          : 'bg-gray-100'
                      }`}>
                        {pais.flag}
                      </div>
                      <span className={`text-sm font-semibold ${
                        formData.disponibilidadGeografica.includes(pais.code)
                          ? 'text-[#1A2B4A]'
                          : 'text-gray-900'
                      }`}>
                        {pais.name}
                      </span>
                      {formData.disponibilidadGeografica.includes(pais.code) && (
                        <i className="ri-check-line text-[#1A2B4A] ml-auto text-xl"></i>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Procesos Tab */}
          {activeTab === 'procesos' && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-briefcase-line text-4xl text-gray-400"></i>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">No tienes procesos activos</h2>
              <p className="text-sm text-gray-600 max-w-md mx-auto leading-relaxed">
                Cuando tu perfil sea seleccionado para un proceso de selección internacional, aparecerá aquí toda la información relevante y el estado del mismo.
              </p>
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-blue-600 text-xl"></i>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-semibold text-blue-900 mb-2">¿Cómo funciona?</p>
                    <p className="text-sm text-blue-800 leading-relaxed">
                      Nuestro equipo revisa constantemente los perfiles registrados. Cuando identifiquemos una oportunidad alineada con tu experiencia y expectativas, te contactaremos directamente para iniciar el proceso de selección.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Recursos Estratégicos Tab */}
          {activeTab === 'recursos' && (
            <div className="space-y-10">

              {user.hasLibraryAccess ? (
                <>
                  {/* Hero de la biblioteca */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F1E35] via-[#1A2B4A] to-[#243550] p-10">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-lg">
                            <i className="ri-book-shelf-line text-white text-xl"></i>
                          </div>
                          <span className="text-white/60 text-xs font-semibold uppercase tracking-widest">Acceso completo desbloqueado</span>
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3 leading-tight">
                          Biblioteca Estratégica Internacional
                        </h2>
                        <p className="text-white/75 text-sm leading-relaxed max-w-xl">
                          Tienes acceso completo a todas las guías profesionales. Cada recurso está diseñado para ayudarte a posicionarte con ventaja en procesos internacionales.
                        </p>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="inline-flex items-center gap-3 bg-green-500/20 border border-green-400/30 rounded-xl px-6 py-4">
                          <div className="w-10 h-10 bg-green-500/30 rounded-full flex items-center justify-center">
                            <i className="ri-check-line text-green-300 text-xl"></i>
                          </div>
                          <div>
                            <p className="text-green-300 text-xs font-semibold uppercase tracking-wide">Acceso activo</p>
                            <p className="text-white text-sm font-bold">Sin caducidad</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Propuesta de valor */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="flex items-start gap-4 bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/10 rounded-lg flex-shrink-0">
                        <i className="ri-archive-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">7 guías especializadas</p>
                        <p className="text-xs text-gray-500 leading-relaxed">CV europeo, entrevistas internacionales y claves culturales por país.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/10 rounded-lg flex-shrink-0">
                        <i className="ri-refresh-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">Actualizaciones incluidas</p>
                        <p className="text-xs text-gray-500 leading-relaxed">El contenido se actualiza con las tendencias del mercado europeo.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4 bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/10 rounded-lg flex-shrink-0">
                        <i className="ri-lock-unlock-line text-[#1A2B4A] text-xl"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">Acceso permanente</p>
                        <p className="text-xs text-gray-500 leading-relaxed">Disponible en tu área privada desde el momento del pago.</p>
                      </div>
                    </div>
                  </div>

                  {/* Contenido de la biblioteca */}
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">Contenido incluido</h3>
                    <p className="text-sm text-gray-500 mb-6">Todo lo que necesitas para prepararte con criterio y diferenciarte en procesos internacionales.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {recursosEstrategicos.map((recurso) => (
                        <div
                          key={recurso.id}
                          className="group flex items-start gap-4 bg-white border border-gray-200 rounded-xl p-5 hover:border-[#1A2B4A]/40 hover:shadow-sm transition-all cursor-pointer"
                          onClick={() => navigate(recurso.ruta)}
                        >
                          <div className="w-10 h-10 flex items-center justify-center bg-[#1A2B4A]/8 rounded-lg flex-shrink-0 group-hover:bg-[#1A2B4A]/15 transition-colors">
                            {recurso.flag ? (
                              <span className="text-xl">{recurso.flag}</span>
                            ) : (
                              <i className={`${recurso.icon} text-[#1A2B4A] text-xl`}></i>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-900 mb-1 leading-snug">{recurso.titulo}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{recurso.descripcion}</p>
                          </div>
                          <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-arrow-right-line text-[#1A2B4A] text-lg"></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Estado bloqueado */}
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 p-10 border-2 border-gray-200">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-gray-300/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-gray-300/10 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>
                    <div className="relative z-10 text-center max-w-2xl mx-auto">
                      <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="ri-lock-line text-gray-400 text-4xl"></i>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">
                        Biblioteca Estratégica Internacional
                      </h2>
                      <p className="text-sm text-gray-600 leading-relaxed mb-8">
                        Desbloquea el acceso completo a 7 guías profesionales diseñadas para posicionarte con ventaja en el mercado laboral europeo. CV europeo, entrevistas internacionales y claves culturales por país.
                      </p>
                      <div className="flex items-center justify-center gap-8 mb-8">
                        <div className="text-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <i className="ri-archive-line text-gray-500 text-xl"></i>
                          </div>
                          <p className="text-xs font-semibold text-gray-700">7 guías</p>
                        </div>
                        <div className="text-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <i className="ri-refresh-line text-gray-500 text-xl"></i>
                          </div>
                          <p className="text-xs font-semibold text-gray-700">Actualizaciones</p>
                        </div>
                        <div className="text-center">
                          <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center mx-auto mb-2">
                            <i className="ri-lock-unlock-line text-gray-500 text-xl"></i>
                          </div>
                          <p className="text-xs font-semibold text-gray-700">Acceso permanente</p>
                        </div>
                      </div>
                      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
                        <div className="flex items-end justify-center gap-1 mb-2">
                          <span className="text-4xl font-extrabold text-[#1A2B4A]">29</span>
                          <span className="text-xl font-bold text-[#1A2B4A] mb-1">€</span>
                        </div>
                        <p className="text-xs text-gray-500">Pago único · Sin suscripción · Acceso inmediato</p>
                      </div>
                      <button
                        onClick={handleRequestLibraryAccess}
                        disabled={isRequestingAccess}
                        className="inline-flex items-center gap-2 px-8 py-4 bg-[#1A2B4A] text-white text-sm font-semibold rounded-xl hover:bg-[#243550] transition-colors cursor-pointer whitespace-nowrap shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isRequestingAccess ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            Enviando solicitud...
                          </>
                        ) : (
                          <>
                            <i className="ri-shopping-bag-line"></i>
                            Solicitar acceso · 29€
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 mt-4">
                        Te contactaremos para completar el proceso de pago y activación
                      </p>
                    </div>
                  </div>

                  {/* Vista previa del contenido bloqueado */}
                  <div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">Contenido incluido</h3>
                    <p className="text-sm text-gray-500 mb-6">Vista previa de los recursos disponibles tras el desbloqueo</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {recursosEstrategicos.map((recurso) => (
                        <div
                          key={recurso.id}
                          className="relative flex items-start gap-4 bg-gray-50 border border-gray-200 rounded-xl p-5 opacity-60"
                        >
                          <div className="w-10 h-10 flex items-center justify-center bg-gray-200 rounded-lg flex-shrink-0">
                            {recurso.flag ? (
                              <span className="text-xl grayscale">{recurso.flag}</span>
                            ) : (
                              <i className={`${recurso.icon} text-gray-400 text-xl`}></i>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-700 mb-1 leading-snug">{recurso.titulo}</p>
                            <p className="text-xs text-gray-500 leading-relaxed">{recurso.descripcion}</p>
                          </div>
                          <div className="absolute top-1/2 right-4 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-gray-300 rounded-full">
                            <i className="ri-lock-line text-gray-600 text-sm"></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

            </div>
          )}

          {/* Editar Tab */}
          {activeTab === 'editar' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-6">Editar información profesional</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre completo
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={formData.nombre}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent"
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sector profesional
                    </label>
                    <select
                      name="sector"
                      value={formData.sector}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent cursor-pointer"
                    >
                      <option value="">Selecciona un sector</option>
                      <option value="Ingeniería">Ingeniería</option>
                      <option value="Salud">Salud</option>
                      <option value="Tecnología">Tecnología</option>
                      <option value="Finanzas">Finanzas</option>
                      <option value="Educación">Educación</option>
                      <option value="Hostelería">Hostelería</option>
                      <option value="Construcción">Construcción</option>
                      <option value="Logística">Logística</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Años de experiencia
                    </label>
                    <select
                      name="experiencia"
                      value={formData.experiencia}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent cursor-pointer"
                    >
                      <option value="">Selecciona experiencia</option>
                      <option value="0-2 años">0-2 años</option>
                      <option value="3-5 años">3-5 años</option>
                      <option value="6-10 años">6-10 años</option>
                      <option value="Más de 10 años">Más de 10 años</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      País de residencia
                    </label>
                    <input
                      type="text"
                      name="pais"
                      value={formData.pais}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1A2B4A] focus:border-transparent"
                      placeholder="Ej: España"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setActiveTab('perfil');
                  }}
                  className="px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-6 py-3 text-sm font-medium text-white bg-[#1A2B4A] rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line mr-2"></i>
                      Guardar cambios
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}